import { Capacitor } from '@capacitor/core';
import { NativeBiometric, AccessControl, BiometryType } from '@capgo/capacitor-native-biometric';

/**
 * Thin wrapper around @capgo/capacitor-native-biometric, scoped to a single
 * "server" identifier for the TofBox login credentials. Credentials are
 * stored with AccessControl.BIOMETRY_ANY so the OS itself (Keychain /
 * Keystore) gates every read behind a live biometric check — the app never
 * sees the stored password without the user passing Face ID/Touch ID/
 * fingerprint at that exact moment.
 */
const SERVER = 'tof-box.app';

// These two checks are supposed to be instant on-device state queries (no
// user interaction), unlike getBiometricLogin() which waits on a live Face
// ID/fingerprint prompt. But on at least one real Android build the native
// bridge call to `isAvailable()` never settled (neither resolved nor
// rejected), which silently stalled the post-login navigation in
// Login.jsx forever — that effect `await`s this before routing to the
// dashboard. A hung availability check must never block login, so both
// functions race against a short timeout and fall back to "unavailable" /
// "not saved" exactly like a caught native error would.
function withTimeout(promise, ms, fallback) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) { settled = true; resolve(fallback); }
    }, ms);
    promise.then(
      (value) => { if (!settled) { settled = true; clearTimeout(timer); resolve(value); } },
      () => { if (!settled) { settled = true; clearTimeout(timer); resolve(fallback); } },
    );
  });
}

export async function getBiometricAvailability() {
  if (!Capacitor.isNativePlatform()) return { isAvailable: false, biometryType: BiometryType.NONE };
  try {
    return await withTimeout(
      NativeBiometric.isAvailable(),
      4000,
      { isAvailable: false, biometryType: BiometryType.NONE },
    );
  } catch {
    return { isAvailable: false, biometryType: BiometryType.NONE };
  }
}

export async function isBiometricLoginSaved() {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await withTimeout(
      NativeBiometric.isCredentialsSaved({ server: SERVER }),
      4000,
      { isSaved: false },
    );
    return result.isSaved;
  } catch {
    return false;
  }
}

export async function saveBiometricLogin(username, password) {
  await NativeBiometric.setCredentials({
    username,
    password,
    server: SERVER,
    accessControl: AccessControl.BIOMETRY_ANY,
  });
}

export async function getBiometricLogin(reason) {
  const { username, password } = await NativeBiometric.getSecureCredentials({
    server: SERVER,
    reason,
  });
  return { username, password };
}

export async function clearBiometricLogin() {
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch { /* nothing stored — fine */ }
}

/**
 * Vault quick-unlock: the vault passphrase lives in its own Keychain /
 * Keystore entry, never on the server. BIOMETRY_CURRENT_SET (stricter than
 * the login entry): enrolling a new face or fingerprint wipes it, so someone
 * who knows the phone's code can't add their own face to open the vault.
 * `username` holds the user_id so an entry is never used for another account.
 */
const VAULT_SERVER = 'vault.tof-box.app';
const VAULT_OWNER_KEY = 'tofbox_vault_bio_owner_v1';

// Reading the Keystore entry needs a biometric prompt, so whether this
// device has one for this user is remembered in localStorage (not secret).
function vaultBioOwner() {
  try { return localStorage.getItem(VAULT_OWNER_KEY); } catch { return null; }
}

export async function isVaultBiometricSaved(userId) {
  if (!userId || vaultBioOwner() !== userId || !Capacitor.isNativePlatform()) return false;
  try {
    const result = await withTimeout(
      NativeBiometric.isCredentialsSaved({ server: VAULT_SERVER }),
      4000,
      { isSaved: false },
    );
    return result.isSaved;
  } catch {
    return false;
  }
}

export async function saveVaultBiometric(userId, passphrase) {
  await NativeBiometric.setCredentials({
    username: userId,
    password: passphrase,
    server: VAULT_SERVER,
    accessControl: AccessControl.BIOMETRY_CURRENT_SET,
  });
  try { localStorage.setItem(VAULT_OWNER_KEY, userId); } catch { /* ignore */ }
}

/** Resolves to the stored passphrase, or rejects: `code` '21' when the
 *  entry is gone (deleted, or wiped by a biometric enrollment change),
 *  '16' (or '11' / '15' / '17') when the prompt was dismissed. */
export async function getVaultBiometric(reason) {
  const { username, password } = await NativeBiometric.getSecureCredentials({
    server: VAULT_SERVER,
    reason,
  });
  return { userId: username, passphrase: password };
}

export async function clearVaultBiometric() {
  try { localStorage.removeItem(VAULT_OWNER_KEY); } catch { /* ignore */ }
  try {
    await NativeBiometric.deleteCredentials({ server: VAULT_SERVER });
  } catch { /* nothing stored — fine */ }
}

/** "Face ID", "Touch ID", or a translated "fingerprint" / "face unlock". */
export function biometryLabel(type, t) {
  if (type === BiometryType.FACE_ID) return 'Face ID';
  if (type === BiometryType.TOUCH_ID) return 'Touch ID';
  if (type === BiometryType.FINGERPRINT) return t('vault.bio.methodFingerprint');
  if (type === BiometryType.FACE_AUTHENTICATION) return t('vault.bio.methodFace');
  return t('vault.bio.methodGeneric');
}

export { BiometryType };
